import { useEffect, useRef, useState } from "react";
import Logo from "../../assets/Images/logo.svg";
import { HeaderString } from "../../utils/string";
import { AiOutlineMenu, AiOutlineClose } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { UserIcon } from "../../assets/images";
import Icon from "../icon/Icon";
import WatchListSidebar from "../../page/watchList/WatchListSidebar";
import { useAuth } from "../../context/authContext";
import { useAuthModal } from "../../context/authModalContext";

const Avatar = ({ givenName, surname }) => {
  const initials = `${givenName?.[0] || ""}${surname?.[0] || ""}`.toUpperCase();
  return (
    <div className="flex items-center justify-center w-12 h-12 bg-[#feba06] text-white font-bold text-xl rounded-full">
      {initials || "U"}
    </div>
  );
};

const LogoutConfirmDialog = ({ onCancel, onConfirm }) => (
  <div style={{ position:"fixed", inset:0, zIndex:9999, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center" }}>
    <div style={{ background:"#fff", borderRadius:12, padding:"28px 32px", width:320, boxShadow:"0 20px 60px rgba(0,0,0,0.3)", textAlign:"center" }}>
      <div style={{ width:48, height:48, borderRadius:"50%", background:"#fef2f2", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
        <svg width="22" height="22" fill="none" stroke="#dc2626" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      </div>
      <div style={{ fontSize:16, fontWeight:700, color:"#111827", marginBottom:8 }}>Log out?</div>
      <p style={{ fontSize:13, color:"#6b7280", marginBottom:24 }}>Are you sure you want to log out of your account?</p>
      <div style={{ display:"flex", gap:10 }}>
        <button onClick={onCancel} style={{ flex:1, padding:"10px 0", borderRadius:8, border:"1.5px solid #e5e7eb", background:"#fff", color:"#374151", fontWeight:600, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
          Cancel
        </button>
        <button onClick={onConfirm} style={{ flex:1, padding:"10px 0", borderRadius:8, border:"none", background:"#FEBA06", color:"#161412", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
          Log Out
        </button>
      </div>
    </div>
  </div>
);

const DropdownMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const givenName = user?.firstName || user?.given_name || user?.name?.split(" ")?.[0] || "";
  const surname = user?.lastName || user?.family_name || user?.name?.split(" ")?.[1] || "";
  const email = user?.email || user?.sub || "";

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className="relative inline-block text-left">
      <div className="flex items-center cursor-pointer" onClick={toggleDropdown}>
        <div className="font-bold text-[#feba06] text-[16px] py-[4px] px-[8px] mx-[2px]">
          Hi, {givenName}
        </div>
        <button className="bg-transparent text-white rounded-full hover:bg-transparent focus:outline-none border border-white flex p-[10px]">
          <img src={UserIcon} alt="" width={"20px"} />
        </button>
      </div>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-max rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 px-4 py-5">
          <div className="py-1 space-y-4" role="menu">
            <div className="flex items-center border rounded-xl p-3 hover:shadow-md transition-shadow">
              <Avatar givenName={givenName} surname={surname} />
              <div className="ml-4">
                <div className="font-semibold text-gray-900 text-base break-words">
                  {givenName} {surname}
                </div>
                <div className="text-sm text-gray-500">{email}</div>
              </div>
            </div>

            <div
              className="font-medium text-gray-900 text-sm cursor-pointer border-t pt-3 mt-3 hover:text-blue-500 transition-colors"
              onClick={() => { navigate("/admin/dashboard"); setIsOpen(false); }}
            >
              {HeaderString.Admin_Dashboard}
            </div>

            <div
              className="font-medium text-gray-900 text-sm cursor-pointer hover:text-red-500 transition-colors"
              onClick={() => { setIsOpen(false); setShowLogoutConfirm(true); }}
            >
              {HeaderString.Logout}
            </div>
          </div>
        </div>
      )}

      {showLogoutConfirm && (
        <LogoutConfirmDialog
          onCancel={() => setShowLogoutConfirm(false)}
          onConfirm={() => { setShowLogoutConfirm(false); logout(); }}
        />
      )}
    </div>
  );
};

const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { openModal } = useAuthModal();

  // A user is treated as "fully logged in" only once onboarding (company +
  // contact) is complete AND admin approval has been granted. Admins bypass
  // the gate. This is why the header keeps showing "Login" while a freshly
  // registered user is filling in the Stepper or waiting for approval.
  const cachedUserData = (() => {
    try { return JSON.parse(localStorage.getItem("getAzureUserData") || "null"); }
    catch { return null; }
  })();
  const roles = Array.isArray(user?.role) ? user.role : (user?.role ? [user.role] : []);
  const isAdmin = roles.includes("Admin");
  const hasOnboarded = Boolean(cachedUserData?.CompanyId && cachedUserData?.ContactId);
  const isPending = cachedUserData?.UserStatus === "Pending";
  const isLoggedIn = isAuthenticated && (isAdmin || (hasOnboarded && !isPending));

  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [watchListSidebar, setWatchListSidebar] = useState(false);
  const [showMobileLogoutConfirm, setShowMobileLogoutConfirm] = useState(false);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const toggleWatchList = () => setWatchListSidebar(!watchListSidebar);

  const givenName = user?.firstName || user?.given_name || user?.name?.split(" ")?.[0] || "";
  const surname = user?.lastName || user?.family_name || user?.name?.split(" ")?.[1] || "";

  return (
    <>
      <div className="bg-[#161412] py-[20px]">
        <div className="flex justify-between custom-container">
          <div>
            <img
              className="cursor-pointer"
              src={Logo}
              alt="Logo"
              height={"40px"}
              width={"auto"}
              onClick={() => navigate("/")}
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center">
            <div className="font-bold text-[#F2F2F2] text-[16px] py-[4px] px-[8px] mx-[2px]">{HeaderString.About}</div>

            {!isLoggedIn && (
              <div
                className="font-bold text-[#F2F2F2] text-[16px] py-[4px] px-[8px] mx-[2px] cursor-pointer"
                onClick={() => openModal("login")}
              >
                {HeaderString.Login}
              </div>
            )}

            <div className="font-bold text-[#F2F2F2] text-[16px] py-[4px] px-[8px] mx-[2px]">
              {HeaderString.Contact}
            </div>

            {isLoggedIn && (
              <>
                <div className="ml-[5px]">
                  <DropdownMenu />
                </div>
                <div className="ml-[8px]">
                  <button
                    onClick={toggleWatchList}
                    className="bg-transparent text-white rounded-full hover:bg-transparent focus:outline-none border border-[#FEBA06] flex p-[10px]"
                  >
                    <Icon
                      icon={"HeroHeart"}
                      className={`text-[20px] fill-[#FEBA06] w-[20px] h-[20px] text-[#FEBA06] cursor-pointer`}
                    />
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Mobile Nav Toggle */}
          <div className="flex md:hidden">
            <button onClick={toggleSidebar} aria-label="Open Sidebar">
              <AiOutlineMenu size={24} color="#F2F2F2" />
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar Overlay */}
      {isSidebarOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50" onClick={toggleSidebar}></div>
      )}

      {/* Mobile Sidebar */}
      <div
        className={`fixed top-0 right-0 w-[250px] h-full bg-[#161412] z-50 transition-transform duration-300 ${
          isSidebarOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex justify-between p-4">
          <button
            onClick={toggleWatchList}
            className="bg-transparent text-white rounded-full hover:bg-transparent focus:outline-none border border-[#FEBA06] flex p-[10px]"
          >
            <Icon
              icon={"HeroHeart"}
              className={`text-[20px] fill-[#FEBA06] w-[20px] h-[20px] text-[#FEBA06] cursor-pointer`}
            />
          </button>
          <button onClick={toggleSidebar} aria-label="Close Sidebar">
            <AiOutlineClose size={24} color="#F2F2F2" />
          </button>
        </div>
        <div className="flex flex-col items-start p-6">
          {isLoggedIn && (
            <div className="py-1 space-y-4 w-full mb-4" role="menu">
              <div className="flex items-center border rounded-xl p-3 hover:shadow-md transition-shadow">
                <Avatar givenName={givenName} surname={surname} />
                <div className="ml-4">
                  <div className="font-semibold text-[#fff] text-sm break-words">
                    {givenName} {surname}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="font-bold text-[#F2F2F2] text-[16px] py-[4px] px-[8px] my-[2px]">{HeaderString.About}</div>
          <div className="font-bold text-[#F2F2F2] text-[16px] py-[4px] px-[8px] my-[2px]">{HeaderString.Contact}</div>

          {!isLoggedIn && (
            <div
              className="font-bold text-[#F2F2F2] text-[16px] py-[4px] px-[8px] my-[2px] cursor-pointer"
              onClick={() => { openModal("login"); toggleSidebar(); }}
            >
              {HeaderString.Login}
            </div>
          )}

          {isLoggedIn && (
            <>
              <div
                className="font-bold text-[#F2F2F2] text-[16px] py-[4px] px-[8px] my-[2px] cursor-pointer"
                onClick={() => { navigate("/admin/dashboard"); toggleSidebar(); }}
              >
                {HeaderString.Admin_Dashboard}
              </div>
              <div
                className="font-bold text-[#F2F2F2] text-[16px] py-[4px] px-[8px] my-[2px] cursor-pointer"
                onClick={() => { toggleSidebar(); setShowMobileLogoutConfirm(true); }}
              >
                {HeaderString.Logout}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="bg-[#FEBA06] py-[4px] text-[#161412] text-[16px]">
        <div
          className="font-medium custom-container text-center cursor-pointer"
          onClick={() => navigate("/admin/add-list")}
        >
          {HeaderString.List_With}
        </div>
      </div>

      {/* WatchList Overlay */}
      {watchListSidebar && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50" onClick={toggleWatchList}></div>
      )}

      {/* WatchList Sidebar */}
      <div
        className={`fixed top-0 right-0 sm:w-[640px] w-full h-full bg-[#161412] z-50 transition-transform duration-300 ${
          watchListSidebar ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex justify-between items-center p-4">
          <div />
          <div className="text-[20px] font-bold text-[#fff]">My WatchList Items</div>
          <button onClick={toggleWatchList} aria-label="Close Sidebar">
            <AiOutlineClose size={24} color="#F2F2F2" />
          </button>
        </div>
        <div className="flex flex-col items-start p-6">
          {watchListSidebar && <WatchListSidebar />}
        </div>
      </div>

      {showMobileLogoutConfirm && (
        <LogoutConfirmDialog
          onCancel={() => setShowMobileLogoutConfirm(false)}
          onConfirm={() => { setShowMobileLogoutConfirm(false); logout(); }}
        />
      )}
    </>
  );
};

export default Header;
